import React, { useEffect, useRef, useState } from 'react';
import './Loader.css';

const Loader = ({ isLoading, setIsLoading }) => {
  const [progress, setProgress] = useState(0);
  const [text, setText] = useState('');
  const [textComplete, setTextComplete] = useState(false);
  const particlesRef = useRef(null);
  const particles = useRef([]);

  const messages = [
    "Initialisation des particules...",
    "Chargement du portfolio...",
    "Configuration des animations...",
    "Préparation de l'interface...",
    "Lancement imminent..."
  ];

  // Effet de texte qui simule du code qui s'écrit
  useEffect(() => {
    if (!isLoading) return;

    const fullText = "const portfolio = {\n  name: 'Hamza TAKARROUHT',\n  role: 'Développeur Full-Stack',\n  loading: true\n};";
    let currentIndex = 0;
    let timer;

    const typeText = () => {
      if (currentIndex < fullText.length) {
        setText(prev => prev + fullText.charAt(currentIndex));
        currentIndex++;
        timer = setTimeout(typeText, Math.random() * 50 + 30);
      } else {
        setTextComplete(true);
      }
    };

    typeText();
    return () => clearTimeout(timer);
  }, [isLoading]);

  // Gestion de la barre de progression
  useEffect(() => {
    if (!isLoading) return;
    
    let interval;
    
    if (progress < 100) {
      interval = setInterval(() => {
        setProgress(prev => {
          // Accélérer progressivement
          const increment = prev < 60 ? 1 : prev < 80 ? 0.7 : prev < 95 ? 0.4 : 0.2;
          const newValue = Math.min(prev + increment, 100);
          
          // Arrondir à 2 décimales
          return Math.round(newValue * 100) / 100;
        });
      }, 70);
    } else if (textComplete) {
      // Quand tout est prêt, on attend un peu puis on masque le loader
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
    
    return () => clearInterval(interval);
  }, [progress, isLoading, textComplete, setIsLoading]);

  // Animation des particules
  useEffect(() => {
    if (!particlesRef.current || !isLoading) return;
    
    const canvas = particlesRef.current;
    const ctx = canvas.getContext('2d');
    
    // Ajuster la taille du canvas
    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);
    
    // Créer les particules
    const numParticles = 80;
    for (let i = 0; i < numParticles; i++) {
      particles.current.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 2 + 1,
        speedX: Math.random() * 2 - 1,
        speedY: Math.random() * 2 - 1,
        opacity: Math.random() * 0.5 + 0.1,
      });
    }
    
    const drawParticles = () => {
      if (!canvas) return;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.current.forEach(particle => {
        // Mouvement
        particle.x += particle.speedX;
        particle.y += particle.speedY;
        
        // Rebond sur les bords
        if (particle.x < 0 || particle.x > canvas.width) particle.speedX *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.speedY *= -1;
        
        // Dessin de la particule
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(179, 135, 246, ${particle.opacity})`;
        ctx.fill();
        
        // Connexion entre particules proches
        particles.current.forEach(otherParticle => {
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 100) {
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            ctx.strokeStyle = `rgba(179, 135, 246, ${0.2 - distance/500})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });
      
      if (isLoading) {
        requestAnimationFrame(drawParticles);
      }
    };
    
    drawParticles();
    
    return () => {
      window.removeEventListener('resize', setCanvasSize);
      particles.current = [];
    };
  }, [isLoading]);

  // Affiche un message en fonction de la progression
  const getMessage = () => {
    if (progress < 20) return messages[0];
    if (progress < 40) return messages[1];
    if (progress < 60) return messages[2];
    if (progress < 80) return messages[3];
    return messages[4];
  };

  if (!isLoading) return null;

  return (
    <div className="loader-container">
      <canvas ref={particlesRef} className="loader-canvas"></canvas>
      
      <div className="loader-content">
        <div className="loader-logo">
          <span className="loader-h">H</span>
          <span className="loader-t">T</span>
        </div>
        
        <div className="loader-code-container">
          <pre className="loader-code-text">{text}<span className="loader-cursor">|</span></pre>
        </div>
        
        <div className="loader-status">
          <div className="loader-message">{getMessage()}</div>
          <div className="loader-progress-container">
            <div className="loader-progress-bar" style={{ width: `${progress}%` }}></div>
          </div>
          <div className="loader-percentage">{Math.floor(progress)}%</div>
        </div>
      </div>
    </div>
  );
};

export default Loader;